import { supabase } from './supabase';
import type {
  Tournament,
  TournamentOrganizer,
  TournamentPlatform,
  TournamentFormat,
  TournamentStatus,
  TournamentVisibility,
  ScheduleItem,
} from './tournaments';

/**
 * Server-side data access for tournaments.
 *
 * Reads from the public `tournaments` table (RLS: public read) and maps the
 * snake_case DB rows to the camelCase `Tournament` shape the UI consumes, so
 * the components stay unchanged whether data is mocked or live.
 */

/** Raw row shape as stored in Postgres. */
interface TournamentRow {
  id: string;
  slug: string;
  title: string;
  game_slug: string;
  game_name: string;
  cover_image: string | null;
  accent_from: string;
  accent_to: string;
  starts_at: string;
  timezone: string;
  registration_deadline: string;
  region: string;
  eligibility: string | null;
  platform: string;
  entry_fee_cents: number;
  prize_pool_cents: number;
  currency: string;
  registered: number;
  max_participants: number;
  team_size: number;
  format: string;
  status: string;
  organizer_name: string;
  organizer_verified: boolean;
  organizer_avatar: string | null;
  description: string;
  rules: string[];
  requirements: string[];
  schedule: ScheduleItem[] | null;
  bracket_url: string | null;
  discord_url: string | null;
  external_registration_url: string | null;
  overstat_match_id?: string | null;
  stream_url?: string | null;
  source: string | null;
  visibility: string | null;
  /**
   * The organizer's profile slug, when the event was imported and its organizer
   * resolved. The read RPCs return it flat as text; a direct table read embeds
   * the related row, so this is either shape and `orgSlug()` normalizes it.
   */
  organization_slug?: string | { slug: string } | null;
  /** Generated column: whether a private event has a password. Never the hash. */
  has_password: boolean | null;
  // Embedded via the tournament_organizers -> users relationship.
  tournament_organizers: {
    sort_order: number;
    users: { id: string; gamertag: string; profile_image_url: string | null } | null;
  }[] | null;
}

function mapOrganizers(row: TournamentRow): TournamentOrganizer[] {
  return (row.tournament_organizers ?? [])
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((o) => o.users)
    .filter((u): u is NonNullable<typeof u> => !!u)
    .map((u) => ({ id: u.id, gamertag: u.gamertag, avatar: u.profile_image_url }));
}

/** Flat text from the RPCs, an embedded row from a direct table select. */
function orgSlug(value: TournamentRow['organization_slug']): string | null {
  if (!value) return null;
  return typeof value === 'string' ? value : (value.slug ?? null);
}

function mapRow(row: TournamentRow): Tournament {
  return {
    // `id` is the DB UUID (used for the registration FK); the UI routes by `slug`.
    id: row.id,
    slug: row.slug,
    title: row.title,
    game: row.game_name,
    gameSlug: row.game_slug,
    coverImage: row.cover_image,
    accent: { from: row.accent_from, to: row.accent_to },
    startsAt: row.starts_at,
    timezone: row.timezone,
    registrationDeadline: row.registration_deadline,
    region: row.region,
    eligibility: row.eligibility ?? undefined,
    platform: row.platform as TournamentPlatform,
    entryFeeCents: row.entry_fee_cents,
    prizePoolCents: row.prize_pool_cents,
    currency: row.currency,
    registered: row.registered,
    maxParticipants: row.max_participants,
    teamSize: row.team_size,
    format: row.format as TournamentFormat,
    status: row.status as TournamentStatus,
    organizer: {
      name: row.organizer_name,
      verified: row.organizer_verified,
      avatar: row.organizer_avatar,
    },
    organizers: mapOrganizers(row),
    description: row.description,
    rules: row.rules ?? [],
    requirements: row.requirements ?? [],
    schedule: row.schedule ?? [],
    bracketUrl: row.bracket_url,
    discordUrl: row.discord_url,
    externalRegistrationUrl: row.external_registration_url,
    overstatMatchId: row.overstat_match_id ?? null,
    streamUrl: row.stream_url ?? null,
    source: row.source ?? undefined,
    organizationSlug: orgSlug(row.organization_slug),
    visibility: (row.visibility as TournamentVisibility) ?? 'public',
    hasPassword: row.has_password ?? undefined,
  };
}

/**
 * Slugs hidden from the public list + sitemap. Still reachable by direct URL
 * via getTournamentBySlug (e.g. an invite-only finals).
 */
const HIDDEN_SLUGS = new Set<string>([]);

/**
 * `status` is the server-derived `status_effective` (a PostgREST computed
 * column on `tournaments`, see migration 20260825000000): nothing writes the
 * stored column after creation, so Postgres flips it to live/completed from
 * `starts_at` at read time. The read RPCs return the same derived value as
 * `status`, so every path through `mapRow` agrees — and so does the mobile app.
 */
const COLUMNS =
  'id, slug, title, game_slug, game_name, cover_image, accent_from, accent_to, starts_at, timezone, registration_deadline, region, eligibility, platform, entry_fee_cents, prize_pool_cents, currency, registered, max_participants, team_size, format, status:status_effective, organizer_name, organizer_verified, organizer_avatar, description, rules, requirements, schedule, bracket_url, discord_url, external_registration_url, overstat_match_id, stream_url, source, visibility, has_password, organization_slug:organizations ( slug ), tournament_organizers ( sort_order, users ( id, gamertag, profile_image_url ) )';

/**
 * Every tournament, public and private, soonest first — the browse list, the
 * sitemap and the calendar.
 *
 * Reads through the `list_tournaments` SECURITY DEFINER RPC rather than the
 * table: RLS hides private rows from direct reads so their `password_hash`
 * can never be selected, and the RPC returns every event without it. Private
 * events show in the list with a lock; the password is only needed to join.
 */
export async function getAllTournaments(): Promise<Tournament[]> {
  const { data, error } = await supabase.rpc('list_tournaments');

  if (error) {
    console.error('Error fetching tournaments:', error.message);
    return [];
  }
  return (data as unknown as TournamentRow[])
    .filter((row) => !HIDDEN_SLUGS.has(row.slug))
    .map(mapRow);
}

/**
 * The list shown in the signed-in app. Private events are public knowledge now
 * (only joining is gated), so this is the same set as the browse list.
 */
export async function getVisibleTournaments(): Promise<Tournament[]> {
  return getAllTournaments();
}

/* ------------------------------------------------------------------ */
/* Creating tournaments (user-facing create flow)                      */
/* ------------------------------------------------------------------ */

/** Fields collected by the public create-tournament form. */
export interface CreateTournamentInput {
  title: string;
  gameSlug: string;
  gameName: string;
  coverImage: string | null;
  accentFrom: string;
  accentTo: string;
  /** Start instant as a UTC ISO string. */
  startsAt: string;
  /** Display timezone label, e.g. "UTC", "PT". */
  timezone: string;
  /** Registration close instant as a UTC ISO string. */
  registrationDeadline: string;
  region: string;
  platform: TournamentPlatform;
  format: TournamentFormat;
  teamSize: number;
  maxParticipants: number;
  entryFeeCents: number;
  prizePoolCents: number;
  description: string;
  /** 'private' hides the event everywhere except its direct link. */
  visibility?: TournamentVisibility;
  /** Optional password gate for a private event. Ignored when public. */
  password?: string | null;
}

/**
 * Create a tournament and link the creator as its primary organizer.
 *
 * Delegates to the `create_user_tournament` SECURITY DEFINER RPC (see migration
 * `20260701120000_tournaments_secure_create`). The RPC stamps ownership
 * (`created_by = auth.uid()`), forces `organizer_verified = false`, derives the
 * organizer identity from the caller's own profile, and generates a unique slug
 * server-side — so none of those trust-sensitive fields can be forged from the
 * client. Direct inserts into `tournaments` / `tournament_organizers` are no
 * longer permitted by RLS. Returns the new slug on success, or an `error`.
 *
 * The `organizer` arg is retained for the caller's signed-in guard; the RPC
 * ignores it and reads the authenticated user instead.
 */
export async function createTournament(
  input: CreateTournamentInput,
  _organizer: { id: string; gamertag: string; avatar: string | null }
): Promise<{ slug: string } | { error: string }> {
  const { data, error } = await supabase.rpc('create_user_tournament', {
    p_title: input.title,
    p_game_slug: input.gameSlug,
    p_game_name: input.gameName,
    p_cover_image: input.coverImage,
    p_accent_from: input.accentFrom,
    p_accent_to: input.accentTo,
    p_starts_at: input.startsAt,
    p_timezone: input.timezone,
    p_registration_deadline: input.registrationDeadline,
    p_region: input.region,
    p_platform: input.platform,
    p_format: input.format,
    p_team_size: input.teamSize,
    p_max_participants: input.maxParticipants,
    p_entry_fee_cents: input.entryFeeCents,
    p_prize_pool_cents: input.prizePoolCents,
    p_description: input.description,
    p_visibility: input.visibility ?? 'public',
    p_password: input.visibility === 'private' ? (input.password ?? null) : null,
  });

  if (error || !data) {
    console.error('createTournament error:', error?.message);
    return { error: error?.message ?? 'Could not create tournament.' };
  }

  return { slug: data as string };
}

/**
 * Update an existing tournament (organizer-only via RLS). Mutable fields only —
 * slug, status, organizer and counts are left untouched.
 */
export async function updateTournament(
  id: string,
  input: CreateTournamentInput
): Promise<{ ok: true } | { error: string }> {
  const { error } = await supabase
    .from('tournaments')
    .update({
      title: input.title,
      game_slug: input.gameSlug,
      game_name: input.gameName,
      cover_image: input.coverImage,
      starts_at: input.startsAt,
      timezone: input.timezone,
      registration_deadline: input.registrationDeadline,
      region: input.region,
      platform: input.platform,
      entry_fee_cents: input.entryFeeCents,
      prize_pool_cents: input.prizePoolCents,
      max_participants: input.maxParticipants,
      team_size: input.teamSize,
      format: input.format,
      description: input.description,
      updated_at: new Date().toISOString(),
    })
    .eq('id', id)

  if (error) {
    console.error('updateTournament error:', error.message)
    return { error: error.message }
  }
  return { ok: true }
}

/**
 * Attach (or clear, with null) the Overstat match id the results page pulls
 * standings from. Plain column update, so the organizer update policy applies.
 */
export async function setOverstatMatchId(
  id: string,
  matchId: string | null
): Promise<{ ok: true } | { error: string }> {
  const { error } = await supabase
    .from('tournaments')
    .update({ overstat_match_id: matchId, updated_at: new Date().toISOString() })
    .eq('id', id)

  if (error) {
    console.error('setOverstatMatchId error:', error.message)
    return { error: error.message }
  }
  return { ok: true }
}

/** Set (or clear) the organizer's livestream link. */
export async function setStreamUrl(
  id: string,
  streamUrl: string | null
): Promise<{ ok: true } | { error: string }> {
  const { error } = await supabase
    .from('tournaments')
    .update({ stream_url: streamUrl, updated_at: new Date().toISOString() })
    .eq('id', id)

  if (error) {
    console.error('setStreamUrl error:', error.message)
    return { error: error.message }
  }
  return { ok: true }
}

/**
 * Change an event's visibility, and optionally its password.
 *
 * Routed through the `set_tournament_visibility` RPC: hashing happens in the
 * database, and a trigger blocks direct writes to `visibility` /
 * `password_hash`, so this is the only way to move an event between public and
 * private. Passing `password` sets a new one; `clearPassword` removes it
 * (leaving a private event reachable by link alone); passing neither keeps the
 * existing password. Switching to public always drops it.
 */
export async function setTournamentVisibility(
  id: string,
  visibility: TournamentVisibility,
  opts: { password?: string | null; clearPassword?: boolean } = {}
): Promise<{ ok: true } | { error: string }> {
  const { error } = await supabase.rpc('set_tournament_visibility', {
    p_tournament_id: id,
    p_visibility: visibility,
    p_password: opts.password ?? null,
    p_clear_password: opts.clearPassword ?? false,
  })

  if (error) {
    console.error('setTournamentVisibility error:', error.message)
    return { error: error.message }
  }
  return { ok: true }
}

/** Delete a tournament (organizer-only via RLS). Child rows cascade. */
export async function deleteTournament(
  id: string
): Promise<{ ok: true } | { error: string }> {
  const { error } = await supabase.from('tournaments').delete().eq('id', id)
  if (error) {
    console.error('deleteTournament error:', error.message)
    return { error: error.message }
  }
  return { ok: true }
}

/**
 * Slug + title for a tournament id. Used to link an event group chat back to
 * its event page, which is routed by slug rather than id.
 */
export async function getTournamentLinkById(
  id: string
): Promise<{ slug: string; title: string } | null> {
  const { data, error } = await supabase
    .from('tournaments')
    .select('slug, title')
    .eq('id', id)
    .maybeSingle();

  if (error) {
    console.error('Error fetching tournament link by id:', error.message);
    return null;
  }
  return data ? { slug: data.slug as string, title: data.title as string } : null;
}

/* ------------------------------------------------------------------ */
/* Private (link-only) events                                          */
/* ------------------------------------------------------------------ */

/** Row shape returned by the `get_private_tournament` RPC. */
type GatedRow = TournamentRow & { needs_password: boolean };

/**
 * Resolve a tournament by slug for anyone holding the link, private or not.
 *
 * Goes through the `get_private_tournament` SECURITY DEFINER RPC rather than a
 * table read, because RLS deliberately hides private rows from the anon key
 * (they must stay out of listings and crawlers). The link itself is the only
 * thing needed to *view* a private event; the password, if any, is checked by
 * `joinTournament` when someone tries to take a spot.
 *
 * Returns null for an unknown slug so the caller can 404.
 */
export async function getLinkedTournament(
  slug: string
): Promise<Tournament | null> {
  const { data, error } = await supabase
    .rpc('get_private_tournament', { p_slug: slug, p_password: null })
    .maybeSingle();

  if (error) {
    console.error('getLinkedTournament error:', error.message);
    return null;
  }
  if (!data) return null;

  const row = data as unknown as GatedRow;

  // The RPC returns scalar columns only; organizers live behind their own
  // table, so fetch them separately (also SECURITY DEFINER, so it works for a
  // private event).
  const { data: orgs } = await supabase.rpc('get_tournament_organizers', {
    p_id: row.id,
  });

  const organizers = ((orgs ?? []) as Array<{
    sort_order: number;
    user_id: string;
    gamertag: string;
    profile_image_url: string | null;
  }>).map((o) => ({
    sort_order: o.sort_order,
    users: {
      id: o.user_id,
      gamertag: o.gamertag,
      profile_image_url: o.profile_image_url,
    },
  }));

  return mapRow({ ...row, tournament_organizers: organizers });
}

/** Postgres `invalid_password` — raised by `join_tournament` on a bad/missing password. */
const INVALID_PASSWORD = '28P01';

/**
 * Register the signed-in user for an event via the `join_tournament` RPC.
 *
 * The RPC is the only write path for password-protected events: the INSERT
 * policy on `tournament_registrations` refuses direct rows for them, and the
 * password is compared against the bcrypt hash inside Postgres. Organizers and
 * invitees are let through without one, so callers should first try with no
 * password and only prompt when `wrongPassword` comes back.
 */
export async function joinTournament(
  tournamentId: string,
  opts: { password?: string | null; status?: 'registered' | 'substitute' } = {}
): Promise<{ error: string | null; wrongPassword: boolean }> {
  const { error } = await supabase.rpc('join_tournament', {
    p_tournament_id: tournamentId,
    p_password: opts.password ?? null,
    p_status: opts.status ?? 'registered',
  });
  if (!error) return { error: null, wrongPassword: false };
  const wrongPassword = error.code === INVALID_PASSWORD;
  return { error: wrongPassword ? null : error.message, wrongPassword };
}

/**
 * Fetch an event for the organizer management surface, bypassing the RLS
 * visibility filter.
 *
 * Server components in this app read with the anon key and no user session, so
 * `auth.uid()` is null during SSR and an RLS-scoped read cannot see a private
 * event even for its own organizer. `get_tournament_for_manage` is a SECURITY
 * DEFINER read that resolves the row by slug without the password gate, so the
 * management form renders for any event including a password-protected one.
 *
 * It exposes no secret (never the password hash) and is only reached from the
 * manage route, whose client component redirects anyone who is not an organizer
 * before rendering any of the management UI.
 */
export async function getTournamentForManage(
  slug: string
): Promise<Tournament | null> {
  const { data, error } = await supabase
    .rpc('get_tournament_for_manage', { p_slug: slug })
    .maybeSingle()

  if (error) {
    console.error('getTournamentForManage error:', error.message)
    return null
  }
  if (!data) return null

  const row = data as unknown as TournamentRow
  const { data: orgs } = await supabase.rpc('get_tournament_organizers', {
    p_id: row.id,
  })

  const organizers = ((orgs ?? []) as Array<{
    sort_order: number
    user_id: string
    gamertag: string
    profile_image_url: string | null
  }>).map((o) => ({
    sort_order: o.sort_order,
    users: {
      id: o.user_id,
      gamertag: o.gamertag,
      profile_image_url: o.profile_image_url,
    },
  }))

  return mapRow({ ...row, tournament_organizers: organizers })
}

/** A single tournament by its URL slug. Returns null if not found. */
export async function getTournamentBySlug(
  slug: string
): Promise<Tournament | null> {
  const { data, error } = await supabase
    .from('tournaments')
    .select(COLUMNS)
    .eq('slug', slug)
    .maybeSingle();

  if (error) {
    console.error('Error fetching tournament by slug:', error.message);
    return null;
  }
  return data ? mapRow(data as unknown as TournamentRow) : null;
}
